import { ActionError, Button, titleCase, toast } from '@westhetics/ui'
import { useId, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { isApiError } from '@/lib/api'
import type { SignInError } from '@/lib/auth'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { routes } from '@/routes'
import { isAcceptablePassword, useAcceptInvite, useInvite } from './api'
import { AuthLayout, AuthSkeleton, FieldNote, PasswordLabel, authInput } from './auth-layout'

/**
 * Aceite do convite (docs/areas/autenticacao.md, "Primeiro acesso por convite"; RN-AUT-05 e
 * docs/api/acesso.md). A pessoa chega pelo link do e-mail (`/invite?t=…`), vê quem foi convidado e
 * para qual clínica, cria a senha e entra. Convite vencido, usado ou reemitido responde uma frase
 * só, com a saída. Se outra conta estava com a sessão aberta neste navegador, a sessão passa a ser
 * a da pessoa convidada (P-AUT-20). Segue o leiaute do login (P-AUT-12).
 */

const MESSAGES = {
  weak: 'A senha precisa de pelo menos 8 caracteres, com letras e números.',
  missingToken: 'Este endereço está sem o convite. Abra o link pelo e-mail que você recebeu.',
} as const

/** A senha ficou gravada, mas `signInWithPassword` falhou em seguida: o caminho é a tela de entrar. */
const SIGN_IN_PROBLEMS: Record<SignInError['kind'], string> = {
  invalid_credentials: 'Entre com o seu e-mail e a senha que acabou de criar.',
  rate_limited: 'Muitas tentativas em pouco tempo. Aguarde um minuto e entre com a senha nova.',
  refused: 'Entre com o seu e-mail e a senha que acabou de criar.',
}

/** O convite não vale (410) ou o token do link veio truncado (422): a mesma tela, com a saída. */
function invalidInviteMessage(error: unknown): string | null {
  if (!isApiError(error)) return null
  if (error.status === 410) return error.message
  return error.fields.t ?? error.fields.token ?? null
}

/**
 * A recusa da senha, venha de onde vier: a validação do corpo responde em `fields.password`; a
 * política de senha do Supabase Auth, em `fields.newPassword`.
 */
function passwordRefusal(error: unknown): string | null {
  const fields = fieldErrors(error)
  return fields.password ?? fields.newPassword ?? null
}

export function InvitePage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const token = params.get('t')
  const invite = useInvite(token)
  const accept = useAcceptInvite()
  const passwordId = useId()
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [touched, setTouched] = useState(false)
  // A recusa da API vale até a pessoa mexer no campo.
  const [refused, setRefused] = useState<string | null>(null)
  // A senha foi gravada, mas a entrada em seguida falhou: o convite já não vale para repetir.
  const [signInProblem, setSignInProblem] = useState<string | null>(null)

  const backToLogin = (
    <Button asChild variant="outline" size="lg" className="mt-7">
      <Link to={routes.login}>Voltar para entrar</Link>
    </Button>
  )

  if (signInProblem) {
    return (
      <AuthLayout title="Senha salva">
        <ActionError
          className="mt-6"
          title="A senha foi salva, mas não foi possível entrar agora"
          description={signInProblem}
        />
        {backToLogin}
      </AuthLayout>
    )
  }

  const invalid = token
    ? (invalidInviteMessage(invite.error) ?? invalidInviteMessage(accept.error))
    : MESSAGES.missingToken
  if (invalid) {
    return (
      <AuthLayout title="Convite indisponível">
        <p className="font-body text-secondary mt-3 text-[13px] leading-[1.6] font-light">
          {invalid}
        </p>
        {backToLogin}
      </AuthLayout>
    )
  }

  if (invite.isError) {
    return (
      <AuthLayout title="Criar sua senha">
        <ActionError
          className="mt-6"
          title="Não foi possível abrir o convite"
          description={errorMessage(invite.error)}
          actions={
            <Button variant="outline" size="sm" onClick={() => void invite.refetch()}>
              Tentar de novo
            </Button>
          }
        />
      </AuthLayout>
    )
  }

  if (!invite.data) {
    return (
      <AuthLayout title="Criar sua senha">
        <AuthSkeleton />
      </AuthLayout>
    )
  }

  const { name, email, clinicName } = invite.data
  const localError = isAcceptablePassword(password) ? null : MESSAGES.weak
  const passwordError = refused ?? (touched ? localError : null)
  // Erro que não é do campo (limite de tentativas, falha de rede) aparece inteiro.
  const generalError =
    accept.isError && !passwordRefusal(accept.error) ? errorMessage(accept.error) : null

  async function submit(event: FormEvent) {
    event.preventDefault()
    setTouched(true)
    if (localError || accept.isPending || !token) return
    let result: Awaited<ReturnType<typeof accept.mutateAsync>>
    try {
      result = await accept.mutateAsync({ token, password })
    } catch (error) {
      setRefused(passwordRefusal(error))
      return
    }
    if (result.signInError) {
      setSignInProblem(result.signInError.message ?? SIGN_IN_PROBLEMS[result.signInError.kind])
      return
    }
    toast('Senha salva.')
    navigate('/', { replace: true })
  }

  return (
    <AuthLayout title="Criar sua senha" onSubmit={submit}>
      <p className="font-display text-name text-ink mt-6 leading-[1.3] font-normal">
        {titleCase(name)}
      </p>
      <p className="font-body text-secondary mt-1 text-[13px] leading-[1.6] font-light break-words">
        {clinicName ? `${clinicName} · ${email}` : email}
      </p>

      {/* O gerenciador de senhas guarda a senha nova junto do e-mail da conta. */}
      <input type="email" autoComplete="username" value={email} readOnly hidden />

      <PasswordLabel
        htmlFor={passwordId}
        shown={showPassword}
        onToggle={() => setShowPassword((value) => !value)}
        className="mt-8"
      >
        Nova senha
      </PasswordLabel>
      <input
        id={passwordId}
        type={showPassword ? 'text' : 'password'}
        autoComplete="new-password"
        value={password}
        aria-invalid={passwordError ? true : undefined}
        aria-describedby={`${passwordId}-hint`}
        onChange={(event) => {
          setPassword(event.target.value)
          setRefused(null)
        }}
        onBlur={() => setTouched(true)}
        className={authInput}
      />
      <FieldNote
        id={`${passwordId}-hint`}
        error={passwordError}
        hint="Pelo menos 8 caracteres, com letras e números."
      />

      {generalError ? (
        <ActionError
          className="mt-6"
          title="Não foi possível salvar a senha"
          description={generalError}
        />
      ) : null}

      <Button
        type="submit"
        size="lg"
        className="mt-7"
        loading={accept.isPending}
        loadingLabel="Salvando…"
      >
        Salvar e entrar
      </Button>
    </AuthLayout>
  )
}
